import { Attachment } from '@billy/design-system'
import qs from 'qs'
import { Side } from 'src/enums/side'

import { uploadFile } from '@features/attachments/query-api'
import { BillState } from '@features/bills/enums/billState'
import { createBill, CreateBillPayload, CreateBillResponseData } from '@features/bills/query-api'
import { Bill } from '@features/bills/types/bill'
import { Contact } from '@features/contacts/types/contact'

import { SortDirection } from '../../enums/sortDirection'
import { Metable } from '../../types/metable'
import { deleteRequest, getRequest, postRequest } from '../../utils'
import { BillsSummaryData } from './routes/BillsList/legacy/types/billsSummaryData'
import { BillsSummaryQueryProps } from './routes/BillsList/legacy/types/billsSummaryQueryProps'
import { BillTimeline } from './types/billTimeline'

export enum BillsSortProperty {
  Amount = 'amount',
  Attachments = 'attachments',
  Balance = 'balance',
  ContactName = 'contact.name',
  CreatedTime = 'createdTime',
  DueDate = 'dueDate',
  EntryDate = 'entryDate',
  GrossAmount = 'grossAmount',
  LineDescription = 'lineDescription',
  SuppliersInvoiceNo = 'suppliersInvoiceNo',
  VoucherNo = 'voucherNo',
}

export interface FetchBillsOptions {
  amount?: number
  contactId?: string
  createdBillId?: string
  currencyId?: string
  /* Examples:
   * - entryDatePeriod=all
   * - entryDatePeriod=dates:2019-04-23...2019-06-14
   * - entryDatePeriod=from:2019-05-08
   * - entryDatePeriod=half:2019-2
   * - entryDatePeriod=month:2019-06
   * - entryDatePeriod=quarter:2019-2
   * - entryDatePeriod=through:2019-05-03
   * - entryDatePeriod=year:2019
   * - entryDatePeriod=fiscalyear:2019
   */
  entryDatePeriod?: string
  externalId?: string
  include?: string
  isBare?: boolean
  isPaid?: boolean
  maxDueDate?: string
  maxEntryDate?: string
  minDueDate?: string
  minEntryDate?: string
  organizationId: string
  q?: string
  page?: number
  pageSize?: number
  sortDirection?: SortDirection
  sortProperty?: BillsSortProperty
  state?: BillState | BillState[]
  suppliersInvoiceNo?: string
}

export interface FetchBillsResponseData extends Metable {
  attachments: Attachment[]
  bills: Bill[]
  contacts?: Contact[]
}

export const fetchBills = ({
  contactId,
  include = 'bill.contact,bill.attachments:embed,attachment.file:embed,bill.lines:embed,bill.balanceModifiers.balanceModifier.modifier:embed,bill.integrationStatus:embed',
  organizationId,
  page,
  pageSize,
  q,
  state,
  ...rest
}: FetchBillsOptions): Promise<FetchBillsResponseData> => {
  const queryParams = qs.stringify({
    organizationId,
    ...(contactId ? { contactId } : {}),
    ...(include ? { include } : {}),
    ...(page ? { page } : {}),
    ...(pageSize ? { pageSize } : {}),
    ...(q ? { q } : {}),
    ...(state ? { state } : {}),
    ...rest,
  })
  return getRequest(`/v2/bills?${queryParams}`)
}

type FetchBillsSummaryOptions = BillsSummaryQueryProps

export interface FetchBillsSummaryResponseData extends Metable {
  summary: BillsSummaryData
}

export const fetchBillsSummary = ({
  contactId,
  entryDatePeriod,
  isOverdue,
  isPaid,
  organizationId,
  q,
}: FetchBillsSummaryOptions): Promise<FetchBillsSummaryResponseData> => {
  const queryParams = qs.stringify({
    ...(contactId ? { contactId } : {}),
    ...(entryDatePeriod ? { entryDatePeriod } : {}),
    ...(isOverdue ? { isOverdue } : {}),
    ...(isPaid ? { isPaid } : {}),
    ...(q ? { q } : {}),
    organizationId,
  })

  return getRequest(`/v2/bills/summary?${queryParams}`)
}

export const createFileAndBill = async (file: File, billData: CreateBillPayload): Promise<CreateBillResponseData> => {
  const apiOptions = {
    'X-Should-Scan': true,
    'X-OrganizationId': billData.organizationId,
  }

  const { files } = await uploadFile(file, apiOptions)
  const billDataExtended: CreateBillPayload = {
    ...billData,
    attachments: [{ organizationId: billData.organizationId, fileId: files[0].id }],
  }

  return createBill(billDataExtended)
}

interface GetNextVoucherNumberResponseData {
  nextVoucherNo: number
}

export const getNextVoucherNumber = (organizationId: string): Promise<GetNextVoucherNumberResponseData> => {
  return getRequest(`/v2/organizations/${organizationId}/nextVoucherNo`)
}

export interface GetBillsBySuppliersInvoiceNoPayload {
  organizationId: string
  contactId: string
  suppliersInvoiceNo: string
}

export const getBillsBySuppliersInvoiceNo = ({
  organizationId,
  contactId,
  suppliersInvoiceNo,
}: GetBillsBySuppliersInvoiceNoPayload): Promise<FetchBillsResponseData> => {
  return getRequest(
    `/v2/bills?organizationId=${organizationId}&contactId=${contactId}&suppliersInvoiceNo=${suppliersInvoiceNo}`,
  )
}

export const deleteBill = (billId: string): Promise<void> => {
  return deleteRequest(`/v2/bills/${billId}`)
}

export interface GetBillTimelinePayload {
  billId: string
  organizationId: string
  pageSize?: number
}

export interface GetBillTimelineResponseData {
  data: BillTimeline[]
}

export const getBillTimeline = ({
  billId,
  organizationId,
  pageSize,
}: GetBillTimelinePayload): Promise<GetBillTimelineResponseData> => {
  const queryParams = qs.stringify({
    pageSize: pageSize || 1000,
  })

  return getRequest(`/organizations/${organizationId}/bills/${billId}/timeline?${queryParams}`)
}

export interface CreateBillTimelinePayload {
  billId: string
  comment: string
  organizationId: string
}

export const createBillTimeline = ({ billId, comment, organizationId }: CreateBillTimelinePayload): Promise<void> => {
  return postRequest(`/organizations/${organizationId}/bills/${billId}/timeline`, {
    data: {
      comment,
    },
  })
}

export interface CreateManualPaymentPayload {
  organizationId: string
  entryDate: string
  cashAmount: number
  cashAccountId: string
  cashSide: Side
  associations: {
    subjectReference: string
  }[]
  cashExchangeRate?: number
  feeAccountId?: string
  feeAmount?: number
}

export const createManualPayment = (payload: CreateManualPaymentPayload): Promise<void> => {
  return postRequest('/v2/bankPayments', { bankPayment: payload })
}

export interface CreatePrepaymentPayload {
  organizationId: string
  contactId: string
  entryDate: string
  amount: number
  associations: {
    subjectReference: string
  }[]
}

export interface CreatePrepaymentResponseData {
  bills: Bill[]
}

export const createPrepayment = (payload: CreatePrepaymentPayload): Promise<CreatePrepaymentResponseData> => {
  return postRequest('/v2/contactBalancePayments', { contactBalancePayment: payload })
}
